import CancelIcon from "@mui/icons-material/Cancel";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  IconButton,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  MetaphoreCollectionSyncLog,
  MetaphoreCollectionSyncLogAsset,
  MetaphoreCollectionSyncLogStatus,
} from "../../../../generated/gql/graphql";
import useCollectionSyncLogAssetLogs from "../../../../hooks/query/sync/use-collection-sync-log-asset-logs";
import useText from "../../../../hooks/use-text";
import {
  getDirectusAssetsPath,
  useDirectus,
} from "../../../../providers/DirectusProvider";
import { LocalizedStrings, SupportedLanguages } from "../../../../utils/lang";

import { formatSyncDate, getSyncStatusColor } from "./sync-log-utils";

const GRID_SIZE = 4;

type ThumbProps = {
  asset: MetaphoreCollectionSyncLogAsset;
  classes?: string;
};

function SyncAssetThumb(props: ThumbProps): ReactElement {
  const { asset, classes } = props;

  const { directus } = useDirectus();
  const [failed, setFailed] = useState(false);

  if (!asset.image || failed) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-300 ${
          classes || ""
        }`}
      >
        <ImageIcon fontSize="inherit" />
      </div>
    );
  }

  return (
    <img
      className={`object-cover bg-gray-100 ${classes || ""}`}
      src={`${getDirectusAssetsPath(directus)}/${
        asset.image
      }?height=100&quality=80&format=png`}
      alt={asset.name || ""}
      onError={() => setFailed(true)}
    />
  );
}

type ThumbnailsProps = {
  assets: MetaphoreCollectionSyncLogAsset[];
  onClick: () => void;
};

export function SyncLogThumbnails(props: ThumbnailsProps): ReactElement {
  const { assets, onClick } = props;

  if (assets.length === 0) {
    return <div className="h-[50px] w-[50px]" />;
  }

  if (assets.length === 1) {
    return (
      <div
        className="h-[50px] w-[50px] shrink-0 cursor-pointer overflow-hidden rounded-[8px]"
        onClick={onClick}
      >
        <SyncAssetThumb asset={assets[0]} classes="h-full w-full" />
      </div>
    );
  }

  const tiles = assets.slice(0, GRID_SIZE);
  const remaining = assets.length - GRID_SIZE;

  return (
    <div
      className="grid h-[50px] w-[50px] shrink-0 cursor-pointer grid-cols-2 gap-[2px] overflow-hidden rounded-[8px]"
      onClick={onClick}
    >
      {tiles.map((asset, index) => {
        const showMore = remaining > 0 && index === GRID_SIZE - 1;
        return (
          <div key={asset.id} className="relative h-full w-full">
            <SyncAssetThumb asset={asset} classes="h-full w-full" />
            {showMore && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-[10px] font-semibold text-white">
                +{remaining + 1}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

type InfoRowProps = {
  label: string;
  children?: ReactElement | string | null;
};

function InfoRow(props: InfoRowProps): ReactElement | null {
  const { label, children } = props;

  if (!children) {
    return null;
  }

  return (
    <div className="flex gap-[10px] text-[13px]">
      <span className="basis-[160px] shrink-0 text-[#999999]">{label}</span>
      <span className="text-[#4A4A4A]">{children}</span>
    </div>
  );
}

type ModalProps = {
  log: MetaphoreCollectionSyncLog;
  isOpen: boolean;
  onClose: () => void;
  /** Admin actions on Scheduled syncs */
  canManage?: boolean;
  actionsDisabled?: boolean;
  onRunNow?: (id: string) => void;
  onCancel?: (id: string) => void;
};

export function SyncLogDetailsModal(props: ModalProps): ReactElement {
  const {
    log,
    isOpen,
    onClose,
    canManage,
    actionsDisabled,
    onRunNow,
    onCancel,
  } = props;

  const { strings } = useText(local);
  const navigate = useNavigate();

  const [loadAssetLogs, { data: assetLogsData, loading: assetLogsLoading }] =
    useCollectionSyncLogAssetLogs();
  const [showErrorLog, setShowErrorLog] = useState(false);

  const assets = log.assets ?? [];
  const isScheduled = log.status === MetaphoreCollectionSyncLogStatus.Scheduled;
  const isFailed = log.status === MetaphoreCollectionSyncLogStatus.Failed;
  const assetLogs = assetLogsData?.collectionSyncLogAssetLogs ?? [];

  function onShowErrorLog(): void {
    setShowErrorLog(true);
    loadAssetLogs({ variables: { collectionSyncLogId: log.id } });
  }

  return (
    <Dialog maxWidth="sm" fullWidth open={isOpen} onClose={onClose}>
      <DialogTitle className="flex items-center justify-between">
        {strings.header.label}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-[5px] pb-[15px]">
          <InfoRow label={strings.status.label}>
            <span className="flex items-center gap-[6px]">
              <CircleIcon
                className={`!h-[12px] !w-[12px] ${getSyncStatusColor(
                  log.status
                )}`}
              />
              {log.status}
            </span>
          </InfoRow>
          <InfoRow label={strings.trigger.label}>
            {log.triggerType && <Chip size="small" label={log.triggerType} />}
          </InfoRow>
          <InfoRow label={strings.scheduledAt.label}>
            {log.scheduledAt && formatSyncDate(log.scheduledAt)}
          </InfoRow>
          <InfoRow label={strings.executedAt.label}>
            {log.executedAt && formatSyncDate(log.executedAt)}
          </InfoRow>
          <InfoRow label={strings.created.label}>
            {log.dateCreated && formatSyncDate(log.dateCreated)}
          </InfoRow>
          <InfoRow label={strings.triggeredBy.label}>
            {log.triggeredByName}
          </InfoRow>
          <InfoRow label={strings.startedBy.label}>{log.startedBy}</InfoRow>
          <InfoRow label={strings.canceledBy.label}>
            {log.canceledByName}
          </InfoRow>
        </div>
        {canManage && isFailed && (
          <div className="mb-[15px] rounded-[8px] bg-red-50 p-[12px]">
            <div className="pb-[4px] text-[13px] font-semibold text-red-700">
              {strings.failureReason.label}
            </div>
            <div className="whitespace-pre-wrap break-words text-[12px] text-[#4A4A4A]">
              {log.errorLog || strings.noFailureReason.label}
            </div>
            {!showErrorLog ? (
              <Button
                size="small"
                color="error"
                className="mt-[8px] capitalize"
                onClick={onShowErrorLog}
              >
                {strings.showErrorLog.label}
              </Button>
            ) : assetLogsLoading ? (
              <CircularProgress size={18} className="mt-[8px]" />
            ) : assetLogs.length === 0 ? (
              <div className="pt-[8px] text-[12px] text-[#999999]">
                {strings.noErrorLog.label}
              </div>
            ) : (
              <div className="flex flex-col gap-[8px] pt-[8px]">
                {assetLogs.map((assetLog) => (
                  <div key={assetLog.id} className="text-[12px]">
                    <div className="flex items-center gap-[6px] pb-[2px]">
                      <Chip size="small" label={assetLog.platform} />
                      <span
                        className={
                          assetLog.status === "Failed"
                            ? "font-semibold text-red-700"
                            : "text-[#4A4A4A]"
                        }
                      >
                        {assetLog.status}
                      </span>
                      <span className="text-[#999999]">
                        {strings.assetLogAsset.label} {assetLog.asset} —{" "}
                        {formatSyncDate(assetLog.dateCreated)}
                      </span>
                    </div>
                    {assetLog.server_response && (
                      <pre className="max-h-[150px] overflow-auto whitespace-pre-wrap break-words rounded-[6px] bg-white p-[8px] text-[11px] text-[#4A4A4A]">
                        {assetLog.server_response}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="pb-[5px] text-[13px] font-semibold text-[#4A4A4A]">
          {strings.assets.label} ({assets.length})
        </div>
        {assets.length === 0 ? (
          <div className="text-[13px] text-[#999999]">
            {strings.noAssets.label}
          </div>
        ) : (
          assets.map((asset) => (
            <div
              key={asset.id}
              className="flex items-center gap-[15px] border-b border-gray-100 py-[8px] last:border-b-0"
            >
              <div className="h-[40px] w-[40px] shrink-0 overflow-hidden rounded-[6px]">
                <SyncAssetThumb asset={asset} classes="h-full w-full" />
              </div>
              <div className="flex grow flex-col overflow-hidden">
                <span className="truncate text-[13px] text-[#4A4A4A]">
                  {asset.name || asset.id}
                </span>
                {(asset.addedByName || asset.addedAt) && (
                  <span className="truncate text-[11px] text-[#999999]">
                    {asset.addedByName &&
                      `${strings.addedBy.label} ${asset.addedByName}`}
                    {asset.addedAt && ` (${formatSyncDate(asset.addedAt)})`}
                  </span>
                )}
              </div>
              <Button
                size="small"
                className="capitalize whitespace-nowrap"
                endIcon={<OpenInNewIcon />}
                onClick={() => navigate(`/file/${asset.id}/1`)}
              >
                {strings.openDetail.label}
              </Button>
            </div>
          ))
        )}
      </DialogContent>
      {canManage && isScheduled && (
        <DialogActions className="px-[24px] pb-[15px]">
          <Button
            size="small"
            variant="outlined"
            className="capitalize rounded-full"
            startIcon={<PlayArrowIcon />}
            disabled={actionsDisabled}
            onClick={() => onRunNow?.(log.id)}
          >
            {strings.runNow.label}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            className="capitalize rounded-full"
            startIcon={<CancelIcon />}
            disabled={actionsDisabled}
            onClick={() => onCancel?.(log.id)}
          >
            {strings.cancelSync.label}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

type LabelString = { label: string };

type LocalSchema = {
  header: LabelString;
  status: LabelString;
  trigger: LabelString;
  scheduledAt: LabelString;
  executedAt: LabelString;
  created: LabelString;
  triggeredBy: LabelString;
  startedBy: LabelString;
  canceledBy: LabelString;
  assets: LabelString;
  noAssets: LabelString;
  addedBy: LabelString;
  openDetail: LabelString;
  runNow: LabelString;
  cancelSync: LabelString;
  failureReason: LabelString;
  noFailureReason: LabelString;
  showErrorLog: LabelString;
  noErrorLog: LabelString;
  assetLogAsset: LabelString;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    header: { label: "Sync details" },
    status: { label: "Status" },
    trigger: { label: "Trigger" },
    scheduledAt: { label: "Scheduled at" },
    executedAt: { label: "Executed at" },
    created: { label: "Created" },
    triggeredBy: { label: "Triggered by" },
    startedBy: { label: "Started manually by admin" },
    canceledBy: { label: "Canceled by" },
    assets: { label: "Assets in this sync" },
    noAssets: { label: "No asset details recorded." },
    addedBy: { label: "added by" },
    openDetail: { label: "Open" },
    runNow: { label: "Run now" },
    cancelSync: { label: "Cancel sync" },
    failureReason: { label: "Failure reason" },
    noFailureReason: {
      label: "No failure reason recorded for this sync.",
    },
    showErrorLog: { label: "Show error log" },
    noErrorLog: {
      label: "No per-asset error logs recorded for this sync.",
    },
    assetLogAsset: { label: "Asset" },
  },
};
